import client from './client';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ApiKeyItem {
  id: string;
  keyPrefix: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface CreateApiKeyResponse {
  id: string;
  key: string;
  keyPrefix: string;
  name: string;
  createdAt: string;
}

export interface LocalRunner {
  vmId: string;
  name: string;
  status: 'READY' | 'ALLOCATED' | 'OFFLINE';
  installedApps: string[];
  lastHeartbeat: string | null;
  createdAt: string | null;
  currentSessionId: string | null;
}

// ── API Key endpoints ────────────────────────────────────────────────────────

export async function createApiKey(name: string): Promise<CreateApiKeyResponse> {
  const response = await client.post('/api/v1/api-keys', { name });
  return response.data;
}

export async function listApiKeys(): Promise<ApiKeyItem[]> {
  const response = await client.get('/api/v1/api-keys');
  return response.data;
}

export async function revokeApiKey(keyId: string): Promise<void> {
  await client.delete(`/api/v1/api-keys/${keyId}`);
}

// ── Local Runner endpoints ───────────────────────────────────────────────────

export async function listLocalRunners(): Promise<LocalRunner[]> {
  const response = await client.get('/api/v1/local-runners');
  return response.data;
}

export async function deleteLocalRunner(vmId: string): Promise<void> {
  await client.delete(`/api/v1/local-runners/${vmId}`);
}

/**
 * Editor-hint summary of how well an assignee set can satisfy a local
 * Task node's required apps, computed from persisted last-seen runner
 * profiles (so it covers offline runners too). Mirrors the backend
 * `RunnerCapabilityCoverage` record.
 */
export interface RunnerCapabilityCoverage {
  requiredApps: string[];
  totalAssignees: number;
  coveredCount: number;
  /** Assignee user ids with a known runner covering every required app. */
  covered: string[];
  /** The rest of the requested assignee set. */
  uncovered: string[];
}

export interface CapabilityCoverageQuery {
  apps: readonly string[];
  userIds?: readonly string[];
  groupId?: string;
}

/**
 * Ask the backend which assignees own a known runner (online or offline)
 * whose installed apps cover the requirement. Org-scoped to the caller;
 * used purely as a non-blocking editor hint under the assignee picker.
 */
export async function getCapabilityCoverage(
  query: CapabilityCoverageQuery,
): Promise<RunnerCapabilityCoverage> {
  const params = new URLSearchParams();
  for (const app of query.apps) params.append('apps', app);
  for (const id of query.userIds ?? []) params.append('userIds', id);
  if (query.groupId) params.append('groupId', query.groupId);
  const response = await client.get(
    `/api/v1/local-runners/capability-coverage?${params.toString()}`,
  );
  return response.data;
}

export interface RunnerDownloadInfo {
  url: string;
  filename: string;
}

export async function getRunnerDownloadUrl(): Promise<RunnerDownloadInfo> {
  const response = await client.get('/api/v1/local-runners/download');
  return response.data;
}

export async function downloadRunner(): Promise<void> {
  const { url, filename } = await getRunnerDownloadUrl();

  if (url.startsWith('/')) {
    const response = await client.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(response.data as Blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(blobUrl);
  } else {
    window.open(url, '_self');
  }
}
